/**
 * Poll voting UI with pre-vote survey and post-vote results: `<PollWidget>` and
 * `<PollModule>`.
 *
 * `<PollWidget>` is the data-connected entry point: it reads the caller's vote
 * (`useMyPollVote`) and the tally (`useCommentStats().breakdowns.poll`), and
 * wires the `onVote` handler (`setPollVoteAsync`, guarded by `needsLogin`) —
 * all through the `poll-votes` oRPC resource. `<PollModule>` is the
 * presentation layer, switching between `<PollSurvey>` and `<PollResult>` based
 * on whether the user has voted. Result bars are absolutely-positioned overlays
 * behind the option label to avoid layout reflow. Pass `showButton=false` to
 * submit immediately on selection (single-click survey mode).
 */

import { Checkmark } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { Center, HoverView, Pad, PadBox, VertBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { DataVizText, TextField, UtilityText } from 'component/text';
import { useCommentStats } from 'data/comment';
import { setPollVoteAsync, useMyPollVote } from 'data/poll-votes';
import { useIsReadOnly } from 'data/spaces';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useDatastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';
import type { TLabel } from 'util/stdtypes';
import { makeTestID, t } from 'util/stdtypes';

import { AnimatedYouTag } from './tag/animated-you-tag';
import { translateLabel, useLanguage } from './translation';

type PollModuleProps = {
    question?: string;
    optionLabels?: TLabel[];
    optionTexts?: string[];
    optionColors?: string[];
    onVote?: (vote: string | null, comment: string | null) => void;
    tallies?: Record<string, number>;
    myVote?: string;
    myComment?: string | null;
    showOtherOption?: boolean;
    showButton?: boolean;
    otherOptionLabel?: TLabel;
    otherOptionText?: string;
    otherOptionPlaceholder?: TLabel;
};

export function PollModule({
    question,
    optionLabels,
    optionTexts,
    optionColors,
    onVote,
    tallies,
    myVote,
    myComment = null,
    showOtherOption = false,
    showButton,
    otherOptionLabel,
    otherOptionText,
    otherOptionPlaceholder,
}: PollModuleProps) {
    const s = usePollModuleStyles();
    const otherOption = otherOptionLabel ?? otherOptionText;
    const votedForOther = myVote ? myVote === otherOption : false;
    const readOnly = useIsReadOnly();
    const noVotes = totalVotes(tallies) === 0;

    if (readOnly && noVotes) {
        return null;
    }

    return (
        <View style={s.pollWrap}>
            <DataVizText text={question || ''} type='large' />
            <Pad size={24} />
            {!myVote && !readOnly && (
                <PollSurvey
                    myComment={myComment}
                    myVote={myVote}
                    onVote={onVote}
                    optionColors={optionColors}
                    optionLabels={optionLabels}
                    optionTexts={optionTexts}
                    otherOptionLabel={otherOptionLabel}
                    otherOptionPlaceholder={otherOptionPlaceholder}
                    otherOptionText={otherOptionText}
                    showButton={showButton}
                    showOtherOption={showOtherOption}
                />
            )}
            {myVote && (
                <PollResult
                    myVote={myVote}
                    optionLabels={optionLabels}
                    optionTexts={optionTexts}
                    otherOptionLabel={otherOptionLabel}
                    otherOptionText={otherOptionText}
                    tallies={tallies}
                    voteComment={myComment}
                    votedForOther={votedForOther}
                />
            )}
            {!myVote && readOnly && <PollResult optionLabels={optionLabels} optionTexts={optionTexts} tallies={tallies} />}
            {myVote && <Pad size={24} />}
            {myVote && !readOnly && (
                <Center>
                    <CTAButton label={t('poll.change_vote_button', 'Change my vote')} onPress={() => onVote?.(null, null)} wide />
                </Center>
            )}
        </View>
    );
}
const usePollModuleStyles = createThemedStyles((tokens) => ({
    pollWrap: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        padding: 20,
        borderRadius: 12,
    },
    subtitle: {
        marginTop: 24,
        marginBottom: 12,
    },
}));

export type PollSurveyProps = {
    optionLabels?: TLabel[];
    optionTexts?: string[];
    optionColors?: string[];
    myVote?: string;
    myComment?: string | null;
    showButton?: boolean;
    onVote?: (option: string, otherValue: string | null) => void;
    showOtherOption?: boolean;
    otherOptionLabel?: TLabel;
    otherOptionText?: string;
    otherOptionPlaceholder?: TLabel;
};

export function PollSurvey({
    optionLabels,
    optionTexts,
    myVote,
    myComment = null as string | null,
    showButton = true,
    onVote,
    showOtherOption = false,
    otherOptionLabel,
    otherOptionText,
    otherOptionPlaceholder,
}: PollSurveyProps) {
    const s = PollSurveyStyle;
    const options = optionLabels ?? optionTexts ?? [];
    const [selectedAnswer, setSelectedAnswer] = useState(myVote);
    const [textfieldValue, setTextfieldValue] = useState(myComment);

    const otherOptionValue = otherOptionLabel ?? otherOptionText ?? '';

    function onSelect(option: any) {
        setSelectedAnswer(option);
        if (!showButton) {
            onSubmit(option);
        }
    }

    function onSubmit(option: any) {
        const optionText = getOptionText(option);
        const otherOptionValueText = getOptionText(otherOptionValue);
        const isOther = optionText === otherOptionValueText;
        onVote?.(optionText, isOther ? textfieldValue : null);
    }

    return (
        <View style={[s.poll]}>
            <VertBox gap={12} role='radiogroup' wide>
                {options.map((option: any, idx: number) => (
                    <PollOptionButton
                        key={getOptionText(option)}
                        label={optionLabels?.[idx]}
                        onPress={() => onSelect(option)}
                        selected={getOptionText(selectedAnswer ?? '') === getOptionText(option)}
                        text={optionTexts?.[idx]}
                    />
                ))}
                {showOtherOption && (
                    <View>
                        <PollOptionButton
                            label={otherOptionLabel}
                            onPress={() => onSelect(otherOptionValue)}
                            selected={getOptionText(selectedAnswer ?? '') === getOptionText(otherOptionValue)}
                            text={otherOptionText}
                        />
                        {getOptionText(selectedAnswer ?? '') === getOptionText(otherOptionValue) && (
                            <PadBox top={12}>
                                <TextField
                                    multiline={false}
                                    onChange={setTextfieldValue}
                                    placeholderLabel={otherOptionPlaceholder}
                                    testID='other-option'
                                    value={textfieldValue}
                                />
                            </PadBox>
                        )}
                    </View>
                )}
            </VertBox>
            {showButton && (
                <PadBox top={24}>
                    <CTAButton
                        disabled={!selectedAnswer}
                        label={t('poll.submit_vote_button', 'Submit my vote')}
                        onPress={() => onSubmit(selectedAnswer)}
                        wide
                    />
                </PadBox>
            )}
        </View>
    );
}
const PollSurveyStyle = StyleSheet.create({
    listWrapSurvey: {
        // paddingBottom: 15, width: '100%',
    },
    poll: {
        alignItems: 'center',
    },
});

type PollOptionButtonProps = {
    label?: TLabel;
    text?: string;
    onPress?: () => void;
    selected?: boolean;
    showResult?: boolean;
    resultPercent?: number;
    comment?: string | null;
};

export function PollOptionButton({
    label,
    text,
    onPress,
    selected = false,
    showResult = false,
    resultPercent,
    comment,
}: PollOptionButtonProps) {
    const { colors } = useTheme();
    const s = usePollOptionButtonStyles();

    const getButtonStyle = (selected: boolean, showResult: boolean) => {
        if (selected && showResult) {
            return s.voted;
        } else if (selected) {
            return s.selected;
        } else {
            return null;
        }
    };

    return (
        <View>
            <HoverView
                aria-checked={selected}
                ariaLabel={label}
                ariaText={text}
                disabled={!onPress || showResult}
                hoverStyle={s.hover}
                onPress={onPress}
                pressedStyle={s.hover}
                role='radio'
                style={[s.button, getButtonStyle(selected, showResult)]}
                testID={makeTestID({ label, text })}
            >
                <View style={s.body}>
                    <UtilityText label={label} text={text} />
                </View>
                {showResult ? (
                    <ResultView comment={comment} percent={resultPercent} s={s} selected={selected} />
                ) : (
                    <VotingView checkColor={colors.colorTextOnPrimaryButton} s={s} selected={selected} />
                )}
            </HoverView>
        </View>
    );
}

const VotingView = ({
    selected,
    s,
    checkColor,
}: {
    selected: boolean;
    s: ReturnType<typeof usePollOptionButtonStyles>;
    checkColor: string;
}) => (
    <View style={[s.rightIcon, selected && s.iconSelected]}>
        <Checkmark color={!selected ? 'transparent' : checkColor} />
    </View>
);

const ResultView = ({
    selected,
    percent,
    comment,
    s,
}: {
    selected: boolean;
    percent?: number;
    comment?: string | null;
    s: ReturnType<typeof usePollOptionButtonStyles>;
}) => {
    return (
        <>
            <View style={s.bar}>
                <View style={[s.filledBar, selected ? s.myBar : s.otherBar, { flex: percent || 0 }]} />
                <View style={{ flex: 100 - (percent || 0) }} />
            </View>
            {selected && <AnimatedYouTag comment={comment} />}

            <Pad size={8} />

            {typeof percent === 'number' && (
                <UtilityText center formatParams={{ percent }} label={t('poll_result.percent', '{percent}%')} type='tiny' weight='medium' />
            )}
        </>
    );
};

const usePollOptionButtonStyles = createThemedStyles((tokens) => ({
    button: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        alignItems: 'center',
        paddingHorizontal: 12,
        height: 40,
        width: '100%',
        borderRadius: 100,
        borderWidth: 1,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderColor: tokens.colors.colorBorderPrimary,
        overflow: 'hidden',
    },
    hover: {
        borderColor: tokens.colors.colorBorderPrimary,
        backgroundColor: '#0000001A',
    },
    voted: {
        borderColor: tokens.colors.colorBorderAccent,
        borderWidth: 1,
    },
    selected: {
        borderColor: tokens.colors.colorButtonPrimary,
    },
    body: {
        flex: 1,
    },
    rightIcon: {
        marginLeft: 10,
        borderColor: tokens.colors.colorBorderPrimary,
        borderRadius: 100,
        borderWidth: 1,
        padding: 4,
    },
    iconSelected: {
        backgroundColor: tokens.colors.colorButtonPrimary,
        borderColor: tokens.colors.colorButtonPrimary,
    },
    bar: {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        zIndex: -1,
    },
    filledBar: {
        height: '100%',
    },
    myBar: {
        backgroundColor: tokens.colors.colorDataVizScale3,
    },
    otherBar: {
        backgroundColor: tokens.colors.colorDataVizOther,
    },
}));

function getOptionText(option: TLabel | string): string {
    return typeof option === 'object' ? (option as TLabel)?.defaultText : option;
}

export type PollResultProps = {
    optionLabels?: TLabel[];
    optionTexts?: string[];
    tallies?: Record<string, number>;
    myVote?: string | null;
    votedForOther?: boolean;
    otherOptionText?: string;
    otherOptionLabel?: TLabel;
    voteComment?: string | null;
};

export function PollResult({
    optionLabels,
    optionTexts,
    tallies,
    myVote,
    votedForOther,
    otherOptionText,
    otherOptionLabel,
    voteComment,
}: PollResultProps) {
    const options = optionLabels ?? optionTexts;
    const language = useLanguage();

    if (!options) return null;
    const total = totalVotes(tallies);
    const tAriaLabel = (votedForOther ? [...options, otherOptionLabel ?? otherOptionText] : options)
        .map((option: any, index: number) => {
            // optionsLabels are translatable, but not optionsTexts, hence why
            // we only translate if optionLabels is given
            const tLabel = optionLabels ? translateLabel({ label: option, language }) : optionTexts?.[index];
            const percent = getPercentAnswer(option, tallies, total);
            if (myVote && myVote === getOptionText(option)) {
                return translateLabel({
                    label: t('poll.result_your_vote', '{tLabel} (your vote): {percent}%'),
                    language,
                    formatParams: { percent: percent || 0, tLabel },
                });
            }
            return translateLabel({
                label: t('poll.result_vote', '{tLabel}: {percent}%'),
                language,
                formatParams: { percent: percent || 0, tLabel },
            });
        })
        .join('; ');

    // Get the translated text for the "Other" option if the user voted for it
    const otherOptionTranslatedText = otherOptionLabel ? translateLabel({ label: otherOptionLabel, language }) : otherOptionText;

    return (
        <VertBox ariaLabel={tAriaLabel} gap={12} role='img' wide>
            {options.map((option: any) => (
                <PollOptionButton
                    key={getOptionText(option)}
                    label={optionLabels ? option : null}
                    resultPercent={getPercentAnswer(option, tallies, total)}
                    selected={myVote === getOptionText(option)}
                    showResult
                    text={optionTexts ? option : null}
                />
            ))}
            {votedForOther && (
                <PollOptionButton
                    comment={voteComment}
                    resultPercent={getPercentAnswer(getOptionText(otherOptionLabel ?? otherOptionText ?? ''), tallies, total)}
                    selected
                    // translate everything already because the user-generated
                    // comment make it so we have to do string + string or
                    // string + Tlabel so no need for the label prop
                    showResult
                    text={otherOptionTranslatedText}
                />
            )}
        </VertBox>
    );
}

function totalVotes(tallies: Record<string, number> | undefined): number {
    if (!tallies) return 0;
    return Object.values(tallies).reduce((sum, n) => sum + n, 0);
}

function getPercentAnswer(answer: TLabel | string, tallies: Record<string, number> | undefined, total: number): number {
    if (!total) return 0;
    const answerText = getOptionText(answer);
    return Math.round(((tallies?.[answerText] ?? 0) * 100) / total);
}

export type PollWidgetProps = {
    question: string;
    optionLabels?: TLabel[];
    optionTexts?: string[];
};

export function PollWidget({ question, optionLabels, optionTexts }: PollWidgetProps) {
    const datastore = useDatastore();
    const { stats } = useCommentStats();
    const myVoteObject = useMyPollVote();

    const tallies = stats?.breakdowns?.poll ?? {};
    const myVote = myVoteObject?.pollOption ?? undefined;

    const onVote = (vote: string | null) => {
        fireAndForget(setPollVoteAsync({ datastore, pollOption: vote ?? '' }));
    };

    return (
        <PollModule
            myVote={myVote}
            onVote={datastore.needsLogin(onVote, 'vote')}
            optionLabels={optionLabels}
            optionTexts={optionTexts}
            question={question}
            tallies={tallies}
        />
    );
}
